/**
 * Task Automation ("Tasks") — shared Zod API contract (BAL-7917).
 *
 * The client-side (Zod) mirror of the BE TypeBox contract
 * (`apps/unified-api/src/domains/tasks/tasks.schema.ts`). Lives in `@ballerine/common` so any FE
 * surface can import it; both sides source their string domains from the same `consts` arrays, so
 * the DB pgEnum, the BE contract, and this mirror can never drift. Read models + the write payloads
 * the client sends. No fetchers/hooks here (endpoint tickets: 7934 list, 7937 detail, 7939).
 *
 * See `docs/plans/BAL-7917-task-automation-backend.md` (§API surface).
 */

import {
  WORKFORCE_ACTOR_TYPES,
  WORKFORCE_AGENT_EXECUTIONS,
  WORKFORCE_CONDITION_TYPES,
  WORKFORCE_EVENTS,
  WORKFORCE_IDEMPOTENCY_SCOPES,
  WORKFORCE_RISK_TYPES,
  WORKFORCE_TASK_KINDS,
  WORKFORCE_TASK_RELATIONSHIPS,
  WORKFORCE_TASK_RESOLUTIONS,
  WORKFORCE_TASK_ROLES,
  WORKFORCE_TASK_RULE_MATCHES,
  WORKFORCE_TASK_STATUSES,
} from '../consts';
import { z } from 'zod';

// ============================================================================
// Enums — sourced from @ballerine/common (single contract source)
// ============================================================================

export const WorkforceTaskStatusSchema = z.enum(WORKFORCE_TASK_STATUSES);
export const WorkforceTaskResolutionSchema = z.enum(WORKFORCE_TASK_RESOLUTIONS);
export const WorkforceTaskKindSchema = z.enum(WORKFORCE_TASK_KINDS);
export const WorkforceActorTypeSchema = z.enum(WORKFORCE_ACTOR_TYPES);
export const WorkforceRiskTypeSchema = z.enum(WORKFORCE_RISK_TYPES);
export const WorkforceEventSchema = z.enum(WORKFORCE_EVENTS);
export const WorkforceTaskRelationshipSchema = z.enum(WORKFORCE_TASK_RELATIONSHIPS);
export const WorkforceIdempotencyScopeSchema = z.enum(WORKFORCE_IDEMPOTENCY_SCOPES);
export const WorkforceConditionTypeSchema = z.enum(WORKFORCE_CONDITION_TYPES);
export const WorkforceAgentExecutionSchema = z.enum(WORKFORCE_AGENT_EXECUTIONS);
export const WorkforceTaskRuleMatchSchema = z.enum(WORKFORCE_TASK_RULE_MATCHES);
export const WorkforceTaskRoleSchema = z.enum(WORKFORCE_TASK_ROLES);

// ============================================================================
// Shared value objects
// ============================================================================

/** Polymorphic actor (B12/F17/F19) — `id` is null for the `system` actor. */
export const WorkforceActorSchema = z.object({
  type: WorkforceActorTypeSchema,
  id: z.string().nullable(),
});

export const WorkforceTaskAssociationSchema = z.object({
  type: z.string(),
  id: z.string(),
});

/** `emit` declarative graph (rule → tasks + relations over rule-local refs, K18). */
export const WorkforceEmitTaskSchema = z.object({
  ref: z.string(),
  definitionId: z.string(),
  role: WorkforceTaskRoleSchema.optional(), // declared by the emit (D3)
  params: z.record(z.string(), z.unknown()).optional(),
});
export const WorkforceEmitRelationSchema = z.object({
  type: WorkforceTaskRelationshipSchema,
  from: z.string(),
  to: z.string(),
});
export const WorkforceEmitSchema = z.object({
  tasks: z.array(WorkforceEmitTaskSchema),
  relations: z.array(WorkforceEmitRelationSchema),
});

// ============================================================================
// Task — read model
// ============================================================================

export const WorkforceTaskSchema = z.object({
  id: z.string(),
  customerId: z.string(),

  definitionId: z.string().nullable(),
  triggerRuleId: z.string().nullable(),
  // `event` lives on the fire (workforce_task_fires), not the task — so it's not on the task shape.
  sourceReportId: z.string().nullable(),
  reportSeriesId: z.string().nullable(),
  businessId: z.string().nullable(),
  riskType: WorkforceRiskTypeSchema.nullable(),
  associations: z.array(WorkforceTaskAssociationSchema),

  teamId: z.string().nullable(),
  assignee: WorkforceActorSchema.nullable(),

  title: z.string(),
  instructions: z.string().nullable(),
  taskKind: WorkforceTaskKindSchema,
  role: WorkforceTaskRoleSchema, // D3

  status: WorkforceTaskStatusSchema,
  resolution: WorkforceTaskResolutionSchema.nullable(),
  resolutionReason: z.string().nullable(),
  cancellationReason: z.string().nullable(),
  output: z.record(z.string(), z.unknown()).nullable(), // written at completion (L5)

  createdBy: WorkforceActorSchema,
  closedBy: WorkforceActorSchema.nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TWorkforceTask = z.infer<typeof WorkforceTaskSchema>;

export const WorkforceTaskLinkSchema = z.object({
  id: z.string(),
  sourceTaskId: z.string(),
  targetTaskId: z.string(),
  relationship: WorkforceTaskRelationshipSchema,
  createdBy: WorkforceActorSchema.nullable(),
  createdByRuleId: z.string().nullable(),
  createdAt: z.string(),
});

export const WorkforceTaskCommentSchema = z.object({
  id: z.string(),
  taskId: z.string(),
  author: WorkforceActorSchema,
  body: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const WorkforceTaskAttachmentSchema = z.object({
  id: z.string(),
  taskId: z.string(),
  commentId: z.string().nullable(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  sha256: z.string().nullable(),
  uploadedBy: WorkforceActorSchema.nullable(), // actor pair (L1)
  createdAt: z.string(),
});

// ============================================================================
// GET /tasks — list (filters + cursor; flat or grouped)
// ============================================================================

export const GetTasksQuerySchema = z.object({
  team: z.string().optional(), // team id, or 'none' for unassigned/triage
  status: z.array(WorkforceTaskStatusSchema).optional(),
  resolution: z.array(WorkforceTaskResolutionSchema).optional(),
  assigneeId: z.string().optional(),
  businessId: z.string().optional(),
  sourceReportId: z.string().optional(),
  riskType: z.array(WorkforceRiskTypeSchema).optional(),
  grouped: z.boolean().optional(),
  cursor: z.string().optional(),
  limit: z.number().min(1).max(200).optional(),
});
export type TGetTasksQuery = z.infer<typeof GetTasksQuerySchema>;

export const WorkforceTaskGroupSchema = z.object({
  parent: WorkforceTaskSchema,
  children: z.array(WorkforceTaskSchema),
  readyForResolution: z.boolean(),
  hasChildren: z.boolean(),
});

export const GetTasksResponseSchema = z.object({
  data: z.array(WorkforceTaskSchema),
  groups: z.array(WorkforceTaskGroupSchema).optional(),
  nextCursor: z.string().nullable(),
});

// ============================================================================
// GET /tasks/summary — counts
// ============================================================================

const CountByKeySchema = z.array(z.object({ key: z.string(), count: z.number() }));
export const GetTasksSummaryResponseSchema = z.object({
  byTeam: CountByKeySchema,
  byRiskType: CountByKeySchema,
  byResolution: CountByKeySchema,
  byStatus: CountByKeySchema,
  reportsWithOpenTasks: z.number(),
  totalOpen: z.number(),
});

// ============================================================================
// GET /tasks/:id — detail (task + unified hydrated activity feed + links + attachments)
// ============================================================================

export const WorkforceActivityItemSchema = z.object({
  id: z.string(),
  action: z.string(),
  actor: WorkforceActorSchema,
  createdAt: z.string(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  comment: WorkforceTaskCommentSchema.optional(),
});

/** Display hydration for the detail header/context (BAL-7937) — additive, optional. */
export const TaskDetailBusinessSchema = z.object({
  id: z.string(),
  companyName: z.string().nullable(),
});
export const TaskDetailSourceReportSchema = z.object({
  id: z.string(),
  reportType: z.string().nullable(),
});

export const GetTaskDetailResponseSchema = z.object({
  task: WorkforceTaskSchema,
  links: z.array(WorkforceTaskLinkSchema),
  attachments: z.array(WorkforceTaskAttachmentSchema),
  activity: z.array(WorkforceActivityItemSchema),
  // --- Additive hydration (BAL-7937; optional, BC-safe) ---
  // Tasks referenced by `links` plus siblings (other children of the same parent), hydrated so the
  // detail page can render the non-blocking "other team also required" banner without extra round-trips.
  linkedTasks: z.array(WorkforceTaskSchema).optional(),
  business: TaskDetailBusinessSchema.optional(), // header subtitle
  sourceReport: TaskDetailSourceReportSchema.optional(), // monitoring-alert link
});
export type TGetTaskDetailResponse = z.infer<typeof GetTaskDetailResponseSchema>;

// ============================================================================
// PATCH /tasks/:id — resolve / transition / assign / reopen
// ============================================================================

/**
 * Server-enforced: `done` on a `requires_resolution` task needs `resolution` + `resolutionReason`;
 * `cancelled` needs `cancellationReason` (B8). All fields optional so one endpoint drives
 * status/assign/reopen.
 */
export const PatchTaskBodySchema = z.object({
  status: WorkforceTaskStatusSchema.optional(),
  resolution: WorkforceTaskResolutionSchema.optional(),
  resolutionReason: z.string().optional(),
  cancellationReason: z.string().optional(),
  teamId: z.string().nullable().optional(),
  assignee: WorkforceActorSchema.nullable().optional(),
});
export type TPatchTaskBody = z.infer<typeof PatchTaskBodySchema>;

/** PATCH /tasks/:id response — the updated task (mirrors the BE TypeBox schema). */
export const PatchTaskResponseSchema = z.object({
  task: WorkforceTaskSchema,
});

// ============================================================================
// Comments · Links · Attachments (write payloads the FE sends)
// ============================================================================

export const CreateTaskCommentBodySchema = z.object({
  body: z.string().min(1),
  attachmentFileIds: z.array(z.string()).optional(),
});

/** POST /tasks/:id/comments response — the created note (mirrors the BE TypeBox schema). */
export const CreateTaskCommentResponseSchema = z.object({
  comment: WorkforceTaskCommentSchema,
});

export const CreateTaskLinkBodySchema = z.object({
  targetTaskId: z.string(),
  relationship: WorkforceTaskRelationshipSchema,
});

export const RegisterTaskAttachmentBodySchema = z.object({
  fileId: z.string(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  sha256: z.string().optional(),
  commentId: z.string().optional(),
});

// ============================================================================
// Definitions (task shape) + Rules (condition + emit) — admin authoring
// ============================================================================

export const WorkforceTaskDefinitionSchema = z.object({
  id: z.string(),
  customerId: z.string().nullable(), // null = public catalog template (K25)
  name: z.string(),
  key: z.string().nullable(), // stable slug — tasks.* addressing key (L5)
  description: z.string().nullable(),
  sourceDefinitionId: z.string().nullable(), // lineage of a catalog copy (K25)
  teamId: z.string().nullable(),
  taskKind: WorkforceTaskKindSchema,
  defaultTitle: z.string(),
  defaultInstructions: z.string().nullable(),
  riskType: WorkforceRiskTypeSchema.nullable(),
  agentExecution: WorkforceAgentExecutionSchema,
  createdBy: WorkforceActorSchema.nullable(), // D7
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const UpsertTaskDefinitionBodySchema = z.object({
  name: z.string().min(1),
  key: z.string().optional(),
  description: z.string().optional(),
  teamId: z.string().nullable().optional(),
  taskKind: WorkforceTaskKindSchema.optional(),
  defaultTitle: z.string().min(1),
  defaultInstructions: z.string().optional(),
  riskType: WorkforceRiskTypeSchema.optional(),
  agentExecution: WorkforceAgentExecutionSchema.optional(),
  isActive: z.boolean().optional(),
});

export const WorkforceTaskRuleSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  event: WorkforceEventSchema,
  reportTypes: z.array(z.string()).nullable(), // NULLABLE (D1)
  condition: z.unknown(), // JsonLogic
  conditionType: WorkforceConditionTypeSchema,
  emit: WorkforceEmitSchema,
  match: WorkforceTaskRuleMatchSchema,
  priority: z.number(),
  idempotencyScope: WorkforceIdempotencyScopeSchema,
  createdBy: WorkforceActorSchema.nullable(), // D7
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const UpsertTaskRuleBodySchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  event: WorkforceEventSchema.optional(),
  reportTypes: z.array(z.string().min(1)).optional(),
  condition: z.unknown(),
  conditionType: WorkforceConditionTypeSchema.optional(),
  emit: WorkforceEmitSchema,
  match: WorkforceTaskRuleMatchSchema.optional(),
  priority: z.number().optional(),
  idempotencyScope: WorkforceIdempotencyScopeSchema.optional(),
  isActive: z.boolean().optional(),
});

/** POST /tasks/rules/:id/simulate — dry-run the graph without writing. */
export const SimulateTaskRuleBodySchema = z.object({
  context: z.record(z.string(), z.unknown()).optional(),
  sourceReportId: z.string().optional(),
});
export const SimulateTaskRuleResponseSchema = z.object({
  willFire: z.boolean(),
  tasks: z.array(z.object({ ref: z.string(), definitionId: z.string(), title: z.string() })),
  relations: z.array(WorkforceEmitRelationSchema),
});

// ============================================================================
// GET /tasks/reports/:reportId/automation — per-report overlay payload
// ============================================================================

export const ReportAutomationRowSchema = z.object({
  riskType: WorkforceRiskTypeSchema.nullable(),
  taskId: z.string(),
  finding: z.string(),
  status: WorkforceTaskStatusSchema,
  resolution: WorkforceTaskResolutionSchema.nullable(),
  effectiveStatus: z.string(),
  resolvedBy: WorkforceActorSchema.nullable(),
  resolvedAt: z.string().nullable(),
  reason: z.string().nullable(),
  openedAt: z.string(),
  closedAt: z.string().nullable(),
});

export const GetReportAutomationResponseSchema = z.object({
  reportId: z.string(),
  readyForResolution: z.boolean(),
  rows: z.array(ReportAutomationRowSchema),
});

/** Light per-report task summary carried by the reports-list endpoint (J17/B6). */
export const ReportTaskSummaryItemSchema = z.object({
  id: z.string(),
  status: WorkforceTaskStatusSchema,
  resolution: WorkforceTaskResolutionSchema.nullable(),
  assignee: WorkforceActorSchema.nullable(),
  taskKind: WorkforceTaskKindSchema,
  role: WorkforceTaskRoleSchema,
});
